import { useEffect, useState } from "react";
import styled from "styled-components";
import axios from "../../axios";
import { colors, font, radius, shadow } from "../../styles/theme/tokens";

// Підсумок оплат за звітний період школи (з 9-го по 8-ме). Дані: GET /admin/payments
const fmt = (n) => Math.round(n).toLocaleString("uk-UA");
const day = (d) => new Date(d).toLocaleDateString("uk-UA", { day: "numeric", month: "short" });

function periodStart(offset) {
  const now = new Date();
  const base = new Date(now.getFullYear(), now.getMonth() - (now.getDate() < 9 ? 1 : 0), 9);
  return new Date(base.getFullYear(), base.getMonth() + offset, 9);
}
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function PaymentsSummary({ refreshKey }) {
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const from = periodStart(offset);
    const to = periodStart(offset + 1);
    setError(false);
    axios
      .get(`admin/payments?from=${iso(from)}&to=${iso(to)}`)
      .then((res) => setData(res.data))
      .catch(() => setError(true));
  }, [offset, refreshKey]);

  const from = periodStart(offset);
  const to = new Date(periodStart(offset + 1).getTime() - 86400000);
  const s = data?.summary;
  const packages = data?.items?.filter((p) => p.type === "package") || [];

  return (
    <Card>
      <Head>
        <Title>Оплати за період {day(from)} – {day(to)}</Title>
        <Nav>
          <NavBtn onClick={() => setOffset(offset - 1)} aria-label="Попередній період">←</NavBtn>
          <NavBtn onClick={() => setOffset(offset + 1)} disabled={offset >= 0} aria-label="Наступний період">→</NavBtn>
        </Nav>
      </Head>
      {error && <Muted>Не вдалося завантажити оплати</Muted>}
      {s && (
        <>
          <Grid>
            <Stat><Value>{fmt(s.revenue)} грн</Value><Label>оплат за період</Label></Stat>
            <Stat><Value>{s.payments}</Value><Label>пакетів</Label></Stat>
            <Stat><Value>{s.newStudents}</Value><Label>нових учнів</Label></Stat>
            <Stat><Value>{s.renewals}</Value><Label>продовжень</Label></Stat>
            <Stat><Value>{s.lessonsSold}</Value><Label>уроків продано</Label></Stat>
          </Grid>
          {packages.length > 0 ? (
            <List>
              {packages.slice(0, 8).map((p) => (
                <Row key={p._id}>
                  <span>{day(p.createdAt)} · <b>{p.studentName}</b>{p.studentCode ? ` (ID ${p.studentCode})` : ""} · {p.lessons} ур.</span>
                  <span>{fmt(p.amount)} грн {p.isRenewal ? <Tag>продовження</Tag> : <Tag $new>новий</Tag>}</span>
                </Row>
              ))}
            </List>
          ) : (
            <Muted>У цьому періоді ще немає оплат, внесених через «Пакет». Облік ведеться з 13.10.2026.</Muted>
          )}
        </>
      )}
    </Card>
  );
}

const Card = styled.div`
  background: ${colors.surface};
  border: 1px solid ${colors.line};
  border-radius: ${radius.lg};
  box-shadow: ${shadow.card};
  padding: 18px 20px;
  margin-bottom: 20px;
  font-family: ${font.text};
  color: ${colors.ink};
`;
const Head = styled.div`display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 14px;`;
const Title = styled.h3`margin: 0; font-size: 1.05rem; font-weight: 800;`;
const Nav = styled.div`display: flex; gap: 6px;`;
const NavBtn = styled.button`
  border: 1px solid ${colors.line}; background: ${colors.surface}; color: ${colors.ink};
  border-radius: ${radius.pill}; width: 34px; height: 34px; cursor: pointer; font-size: 1rem;
  &:disabled { opacity: .35; cursor: default; }
`;
const Grid = styled.div`display: grid; grid-template-columns: repeat(auto-fill, minmax(130px, 1fr)); gap: 10px;`;
const Stat = styled.div`background: ${colors.brandSoft}; border-radius: ${radius.md}; padding: 12px;`;
const Value = styled.div`font-size: 1.3rem; font-weight: 800; color: ${colors.brandDark};`;
const Label = styled.div`font-size: .8rem; color: ${colors.muted};`;
const List = styled.div`margin-top: 14px; border-top: 1px solid ${colors.line};`;
const Row = styled.div`
  display: flex; justify-content: space-between; gap: 10px; flex-wrap: wrap;
  padding: 8px 0; border-bottom: 1px solid ${colors.line}; font-size: .9rem;
`;
const Tag = styled.span`
  margin-left: 6px; padding: 2px 8px; border-radius: ${radius.pill}; font-size: .75rem; font-weight: 700;
  background: ${(p) => (p.$new ? colors.successSoft : colors.amberSoft)};
  color: ${(p) => (p.$new ? colors.success : colors.amberDeep)};
`;
const Muted = styled.p`color: ${colors.muted}; font-size: .9rem; margin: 10px 0 0;`;
